/*
 * LocalRecordingManager
 * ----------------------------------------------
 * Authoritative client-side module for local recording.
 * This initial skeleton wires up the public surface and an
 * EventTarget-based pub/sub layer but keeps all side-effects as TODOs.
 * Intended to be a platform-agnostic interface and usable in the future by desktop, web, and mobile local recording implementations.
 */
/* eslint-disable @typescript-eslint/no-empty-function */
import type { RecordingState } from "~desktop/shared/desktop-recorder-types";

import {
	propagatePauseRecording as gqlPropagatePauseRecording,
	propagateResumeRecording as gqlPropagateResumeRecording,
} from "~/lib/desktopRecorderApi";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";

// ------------------------------------------------------------------
// Public Event Types
// ------------------------------------------------------------------

type StatusChangedEventType = "started" | "paused" | "resumed" | "stopped" | "muted" | "unmuted" | "stateChanged";

export class StatusChangedEvent extends Event {
	constructor(
		type: StatusChangedEventType,
		public readonly state: RecordingState,
	) {
		super(type);
	}
}

export class AmplitudeEvent extends Event {
	constructor(public readonly amplitude: number) {
		super("amplitude");
	}
}

interface LocalRecordingManager extends EventTarget {
	// Overloads for strongly-typed event listeners
	addEventListener(
		type: StatusChangedEventType,
		listener: (event: StatusChangedEvent) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	addEventListener(
		type: "amplitude",
		listener: (event: AmplitudeEvent) => void,
		options?: boolean | AddEventListenerOptions,
	): void;
	addEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject | null,
		options?: boolean | AddEventListenerOptions,
	): void;

	removeEventListener(
		type: StatusChangedEventType,
		listener: (event: StatusChangedEvent) => void,
		options?: boolean | EventListenerOptions,
	): void;
	removeEventListener(
		type: "amplitude",
		listener: (event: AmplitudeEvent) => void,
		options?: boolean | EventListenerOptions,
	): void;
	removeEventListener(
		type: string,
		listener?: EventListenerOrEventListenerObject | null,
		options?: boolean | EventListenerOptions,
	): void;
}

class LocalRecordingManager extends EventTarget {
	// Singleton -------------------------------------------------------
	private static _instance: LocalRecordingManager | null = null;

	/**
	 * Retrieves the singleton instance, creating it if necessary.
	 */
	public static getInstance(): LocalRecordingManager {
		if (!this._instance) {
			this._instance = new LocalRecordingManager();
		}
		return this._instance;
	}

	/**
	 * Replace the current singleton with a fresh instance.
	 * Useful for Jest test isolation.
	 */
	public static resetForTests(): void {
		this._instance?.dispose();
		this._instance = null;
	}

	// Rough heuristic: only the first window with `window.desktopRecorder` and
	// *without* `window.floatingNote` is treated as the main renderer.
	private readonly isMainRenderer: boolean =
		typeof window !== "undefined" && "desktopRecorder" in window && !("floatingNote" in window);

	private currentState: RecordingState = { status: "idle" };

	private constructor() {
		super();
		this.registerNativeEventStream();

		// Initialize state from the desktop recorder, if available
		const maybeState = window.DesktopRecorderAPI?.getState?.();
		if (maybeState) {
			maybeState
				.then(state => {
					this.onIpcStateUpdate(state);
				})
				.catch(console.error);
		}
	}

	/**
	 * Cleanly tear down listeners (mainly for tests / hot-reload).
	 */
	public dispose(): void {
		// FIXME: we need to clean up listeners here.
	}

	/**
	 * Retrieve the current recording state synchronously.
	 */
	public getCurrentState(): RecordingState {
		return this.currentState;
	}

	// ----------------------------------------------------------------
	// Event subscription helpers (thin wrapper around EventTarget)
	// ----------------------------------------------------------------

	// Convenience alias for parity with `on()` from popular emitter APIs
	// Preserve overloads by explicitly typing the bound function
	public on: LocalRecordingManager["addEventListener"] = this.addEventListener.bind(
		this,
	) as unknown as LocalRecordingManager["addEventListener"];

	private registerNativeEventStream(): void {
		const native = window.DesktopRecorderAPI;
		if (!native) return;

		native.registerEventListener("started", state => this.onIpcStateUpdate(state, "started"));
		native.registerEventListener("paused", state => this.onIpcStateUpdate(state, "paused"));
		native.registerEventListener("resumed", state => this.onIpcStateUpdate(state, "resumed"));
		native.registerEventListener("muted", state => this.onIpcStateUpdate(state, "muted"));
		native.registerEventListener("unmuted", state => this.onIpcStateUpdate(state, "unmuted"));
		native.registerEventListener("stopped", state => this.onIpcStateUpdate(state, "stopped"));
		native.registerEventListener("amplitude", amp => this.dispatchEvent(new AmplitudeEvent(amp)));
	}

	/**
	 * Unified IPC event handler – every native event now passes the full
	 * recording state. Simply transition to the new state.
	 */
	private onIpcStateUpdate(next: RecordingState, sourceEvent?: StatusChangedEventType): void {
		console.log("onIpcStateUpdate", next, sourceEvent);

		const previous = this.currentState;
		this.currentState = next;

		// Main renderer remote control management
		if (this.isMainRenderer) {
			// Start listening for remote controls when recording starts
			if (previous.status === "idle" && next.status !== "idle" && next.meetingRecordingId) {
				this.listenForRemoteControls(next.meetingRecordingId);
			}
			// Stop listening when recording stops
			else if (previous.status !== "idle" && next.status === "idle") {
				this.stopListening();
			}
		}

		// Emit granular events
		if (next.status === "paused" && previous.status !== "paused") {
			this.dispatch("paused");
		} else if (next.status === "recording" && previous.status === "paused") {
			this.dispatch("resumed");
		} else if (next.status === "idle" && previous.status !== "idle") {
			this.dispatch("stopped");
		}

		if (next.status !== "idle" && previous.status !== "idle" && next.muted !== previous.muted) {
			this.dispatch(next.muted ? "muted" : "unmuted");
		}

		// Forward pause/resume mutations if necessary
		if (this.isMainRenderer && next.status !== "idle" && next.meetingRecordingId) {
			if (previous.status === "recording" && next.status === "paused") {
				gqlPropagatePauseRecording(next.meetingRecordingId).catch(console.error);
			} else if (previous.status === "paused" && next.status === "recording") {
				gqlPropagateResumeRecording(next.meetingRecordingId).catch(console.error);
			}
		}

		// Generic stateChanged event
		this.dispatch("stateChanged");
	}

	// ----------------------------------------------------------------
	// Public action surface
	// ----------------------------------------------------------------

	public pause(): void {
		if (!window.DesktopRecorderAPI) {
			throw new Error("LocalRecordingManager.pause(): DesktopRecorderAPI not available");
		}
		window.DesktopRecorderAPI.pause();
	}

	public resume(): void {
		if (!window.DesktopRecorderAPI) {
			throw new Error("LocalRecordingManager.resume(): DesktopRecorderAPI not available");
		}
		window.DesktopRecorderAPI.resume();
	}

	public stop(confirm = true): void {
		if (!window.DesktopRecorderAPI) {
			throw new Error("LocalRecordingManager.stop(): DesktopRecorderAPI not available");
		}
		window.DesktopRecorderAPI.stop(confirm);
	}

	public mute(): void {
		if (!window.DesktopRecorderAPI) {
			throw new Error("LocalRecordingManager.mute(): DesktopRecorderAPI not available");
		}
		window.DesktopRecorderAPI.mute();
	}

	public unmute(): void {
		if (!window.DesktopRecorderAPI) {
			throw new Error("LocalRecordingManager.unmute(): DesktopRecorderAPI not available");
		}
		window.DesktopRecorderAPI.unmute();
	}

	public listenForRemoteControls(meetingRecordingId: string): void {
		if (this.remoteEmitter) {
			// already listening, switch group if needed
			if (this.connectedMeetingRecordingId === meetingRecordingId) return;
			this.stopListening();
		}

		this.connectedMeetingRecordingId = meetingRecordingId;
		this.remoteEmitter = WebsocketClient.getInstance().getOrJoinGroup(
			this.getRemoteControlsGroupName(meetingRecordingId),
		);

		this.remoteEmitter.on("message", (message: { type: string }) => {
			switch (message.type) {
				case "PAUSE_RECORDING":
					this.pause();
					break;
				case "RESUME_RECORDING":
					this.resume();
					break;
				case "STOP_RECORDING":
					// Don't show the confirmation dialog when stopping from the remote controls
					this.stop(false);
					break;
				default:
					break;
			}
		});

		// keep connection alive
		this.pingInterval = window.setInterval(() => {
			this.remoteEmitter?.send({ type: "PING" });
		}, 5000);
	}

	public stopListening(): void {
		if (this.pingInterval) {
			clearInterval(this.pingInterval);
			this.pingInterval = null;
		}
		if (this.remoteEmitter && this.connectedMeetingRecordingId) {
			this.remoteEmitter.socketClient.leaveGroup(this.getRemoteControlsGroupName(this.connectedMeetingRecordingId));
			this.remoteEmitter = undefined;
			this.connectedMeetingRecordingId = null;
		}
	}

	private remoteEmitter?: GroupEmitter;
	private connectedMeetingRecordingId: string | null = null;
	private pingInterval: number | null = null;

	private getRemoteControlsGroupName = (id: string) => `botless_recordings.${id}`.replace(/=/g, "");

	private dispatch<T extends StatusChangedEventType>(type: T): void {
		const event = new StatusChangedEvent(type, this.currentState);
		this.dispatchEvent(event);
	}
}

// Export the singleton instance by default to keep consumer syntax concise
export default LocalRecordingManager.getInstance();
